import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Custom, Modal, Progress, Form } from "components";
import { Enums, Helpers, Routes } from "utils";
import { Box, IconButton } from "@mui/material";
import { PairValue } from "types";
import { IClinic, IPatient } from "interfaces";
import ForumIcon from "@mui/icons-material/Forum";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PasswordIcon from "@mui/icons-material/Password";

type Props = {
  // TODO: it may be improved (currently being used to force the updated of the appointments)
  incrementComponent?: number;
  onClose?: () => void;
  onChange?: (countPatients: number) => void;
  opened?: boolean;
  toggle?: () => void;
};

export const Patient = (props: Props) => {
  const { t, service, feedback, setFeedback } = useApp();

  const [isLoading, setIsLoading] = useState(true);
  const [currentLength, setCurrentLength] = useState(0);
  const [patients, setPatients] = useState<IPatient[]>([]);
  const [clinics, setClinics] = useState<PairValue[]>([]);
  const [rows, setRows] = useState<number[]>([]);
  const [patient, setPatient] = useState<IPatient | null>(null);
  const [profileOpened, setProfileOpened] = useState(false);
  const [reminderOpened, setReminderOpened] = useState(false);
  const [resetPasswordOpened, setResetPasswordOpened] = useState(false);

  let initialRender = useRef(true);

  useEffect(() => {
    patientsEffect();
    if (initialRender.current) {
      clinicsEffect();
      initialRender.current = false;
    }
  }, [props.incrementComponent]);

  const countPatients = patients?.length ?? 0;

  const columns = [
    {
      field: "username",
      width: 200,
      sortable: false,
      headerName: t.label.name,
      renderCell: ({ row }: { row: IPatient }) =>
        Helpers.replaceComma(row.username),
    },
    { field: "email", headerName: t.label.email, width: 200 },
    {
      field: "clinic",
      width: 230,
      sortable: false,
      headerName: t.label.home_clinic,
      renderCell: ({ row }: { row: IPatient }) => {
        return (
          <Custom.Select
            items={clinics}
            value={row.clinicId}
            onChange={(clinicId: number) =>
              handleClinicChange(row.patientId, clinicId)
            }
          />
        );
      },
    },
    {
      field: "actions",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      headerName: t.label.actions,
      renderCell: ({ row }: { row: IPatient }) => {
        return (
          <>
            <IconButton
              size="medium"
              color="error"
              onClick={() => handleStateChange(row.patientId, !row.active)}
            >
              {row.active ? <PersonIcon /> : <PersonOffIcon />}
            </IconButton>
            <IconButton
              size="medium"
              color="inherit"
              onClick={() => handleProfile(row)}
            >
              <BadgeIcon />
            </IconButton>
            <IconButton
              size="medium"
              color="inherit"
              onClick={() => handleValidate(row)}
            >
              {row.hcValid ? <CheckCircleIcon /> : <UnpublishedIcon />}
            </IconButton>
            <IconButton
              size="medium"
              color="inherit"
              href={handleNavigate(row.patientId)}
            >
              <ForumIcon />
            </IconButton>
            <IconButton
              size="medium"
              color="inherit"
              onClick={() => handleResetPassword(row)}
            >
              <PasswordIcon />
            </IconButton>
          </>
        );
      },
    },
  ];

  const patientsEffect = async () => {
    setIsLoading(true);
    const response = await service.patient.find();
    setIsLoading(false);
    setPatients(response);
    props.onChange && props.onChange(countPatients);
  };

  const clinicsEffect = async () => {
    setIsLoading(true);
    const response = await service.clinic.all();
    const clinics = response.map(
      (item: IClinic) =>
        ({ key: item.clinicId, value: item.username } as PairValue)
    );
    setClinics(clinics);
  };

  const handleSelectRow = (rows: number[]): void => setRows(rows);

  const handleNavigate = (patientId: number): string =>
    Helpers.interpolateURL(Routes.clinic.chat, {
      id: patientId,
      userType: Enums.EnumUser.Patient,
    });

  const handleStateChange = async (patientId: number, state: boolean) => {
    await service.patient.updateState(patientId, state);
    patientsEffect();
  };

  const handleProfile = async (patient: IPatient) => {
    setPatient(patient);
    handleProfileOpen();
  };

  const handleResetPassword = async (patient: IPatient) => {
    setPatient(patient);
    handleResetPasswordOpen();
  };

  const handleValidate = async (patient: IPatient) => {
    await service.patient.updateValid(patient.patientId, !patient.hcValid);
    patientsEffect();
  };

  const handleClinicChange = async (patientId: number, clinicId: number) => {
    await service.patient.updateClinic(patientId, clinicId);
    patientsEffect();
  };

  const handleProfileOpen = () => setProfileOpened(true);
  const handleProfileClose = () => setProfileOpened(false);

  const handleResetPasswordOpen = () => setResetPasswordOpened(true);

  const handleResetPasswordClose = () => {
    setResetPasswordOpened(false);
    setFeedback(null);
  };

  const handleReminderOpen = () => setReminderOpened(true);

  const handleReminderClose = () => {
    setReminderOpened(false);
    props.onClose && props.onClose();
  };

  return (
    <Custom.Card
      title={t.title.clinic.patient}
      action={
        <>
          <Custom.Button
            size="small"
            variant="outlined"
            disabled={rows.length === 0}
            onClick={handleReminderOpen}
          >
            {t.action.create_reminder}
          </Custom.Button>
          <Modal.CreateReminder
            users={rows}
            opened={reminderOpened}
            onClose={handleReminderClose}
          />
        </>
      }
      opened={props.opened}
      toggle={props.toggle}
    >
      <Box sx={{ position: "relative" }}>
        {patients ? (
          <>
            <Custom.DataGrid
              columns={columns}
              rows={patients}
              isLoading={isLoading}
              pageSize={25}
              quickSearch
              getRowId={(row: IPatient) => row.patientId}
              onSelect={handleSelectRow}
            />
            <Modal.Profile
              patient={patient}
              opened={profileOpened}
              onClose={handleProfileClose}
            />
            <Custom.Modal
              header={t.title.auth.reset_password}
              opened={resetPasswordOpened}
              message={feedback?.message ?? ""}
              severity={feedback?.severity ?? undefined}
              onClose={handleResetPasswordClose}
            >
              <Form.ResetPasswordAdmin
                patient={patient}
                onClose={handleResetPasswordClose}
              />
            </Custom.Modal>
          </>
        ) : (
          <Progress.NoRecord height={200} />
        )}
      </Box>
    </Custom.Card>
  );
};
